import React, { createContext, useState, useEffect } from 'react';
import { http } from "../api/http";

interface User {
  email: string;
  name: string;
  store_id: string;
}

interface AuthContextProps {
  user: User | null;
  setUser: (user: User | null) => void;
  logout: () => void;
  loading: boolean;
}

export const AuthContext = createContext<AuthContextProps>({
  user: null,
  setUser: () => {},
  logout: () => {},
  loading: true,
});

export const AuthProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [user, setUser] = useState<User | null>(null);
  const [loading, setLoading] = useState(true);

useEffect(() => {
  const bootstrapAuth = async () => {
    try {
      const token = localStorage.getItem("authToken");

      if (token) {
        try {
          const response = await http.get("/auth/validate-token");
          const { id, name, email, location_id } = response.data.user;
          const storeId = String(location_id || 1);

          setUser({
            name,
            email,
            store_id: storeId,
          });

          localStorage.setItem("store_id", storeId);
          localStorage.setItem(
            "adminUser",
            JSON.stringify({
              id,
              name,
              email,
              store_id: storeId,
            })
          );

          setLoading(false);
          return;
        } catch {
          // access token invalid/expired, continue to refresh attempt
        }
      }

      const refreshResponse = await http.post("/auth/refresh");
      const nextToken = refreshResponse.data?.token;
      const refreshedUser = refreshResponse.data?.user;

      if (!nextToken || !refreshedUser) {
        throw new Error("Refresh failed");
      }

      const storeId = String(refreshedUser.location_id || 1);

      localStorage.setItem("authToken", nextToken);
      localStorage.setItem("store_id", storeId);
      localStorage.setItem(
        "adminUser",
        JSON.stringify({
          id: refreshedUser.id,
          name: refreshedUser.name,
          email: refreshedUser.email,
          store_id: storeId,
        })
      );

      setUser({
        name: refreshedUser.name,
        email: refreshedUser.email,
        store_id: storeId,
      });
    } catch {
      localStorage.removeItem("authToken");
      localStorage.removeItem("store_id");
      localStorage.removeItem("adminUser");
      setUser(null);
    } finally {
      setLoading(false);
    }
  };

  bootstrapAuth();
}, []);

  const logout = async () => {
  try {
    await http.post("/auth/logout");
  } catch {}

  localStorage.removeItem("authToken");
  localStorage.removeItem("store_id");
  localStorage.removeItem("adminUser");
  setUser(null);
};

  return (
    <AuthContext.Provider value={{ user, setUser, logout, loading }}>
      {children}
    </AuthContext.Provider>
  );
};
